import { useMutation } from '@apollo/client'
import { Input, message } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { GET_MOBILE_CODE, SET_TRADE_PWD } from '~/graphqls/gqls'
import { Captcha } from './Captcha'
import CryptoJS from 'crypto-js'
import { MyForm } from '~/components/ui/MyForm'
import { MyModal } from '~/components/ui/MyModal'
import { graphQLErrorMessage, isTradePwd, showMobile } from '~/utils'
import { MyBtn } from '~/components/ui/MyBtn'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'
import {
  getMobileCode,
  getMobileCodeVariables,
  setTradePwd,
  setTradePwdVariables,
  VerifyCodeScene,
} from '~/graphqls/types'

export const SetTradePwdModal: React.FC = observer(() => {
  const [form] = MyForm.useForm()
  const { store } = useStores()

  const [loading, setLoading] = useState(false)

  const visible = useMemo(() => {
    return !!store.modal?.setTradePwd
  }, [store.modal])

  const setVisible = useCallback(
    (visible: boolean) => {
      store.modal?.setTradePwdVisible(visible)
    },
    [store.modal]
  )

  const initialValues = {
    mobile: showMobile(store.userinfo?.base?.mobile),
  }

  const [getMobileCodeAction] = useMutation<
    getMobileCode,
    getMobileCodeVariables
  >(GET_MOBILE_CODE)
  const [setTradePwdByMobileAction] = useMutation<
    setTradePwd,
    setTradePwdVariables
  >(SET_TRADE_PWD)

  const confirmPwd = useCallback(
    (_rule, value) => {
      const pwd = form.getFieldValue('password')
      if (!value) {
        return Promise.reject('请再次输入交易密码')
      } else if (value !== pwd) {
        return Promise.reject('两次交易密码输入不一致')
      } else {
        return Promise.resolve()
      }
    },
    [form]
  )

  const onMobileCode = useCallback(
    validInfo => {
      getMobileCodeAction({
        variables: {
          mobile: store.userinfo?.base?.mobile as string,
          scene: VerifyCodeScene.CHANGE_PWD,
          behaviorParam: {
            token: validInfo.token,
            authenticate: validInfo.authenticate,
          },
        },
      })
        .then(() => {
          message.destroy()
          message.success('获取验证码成功')
        })
        .catch(error => {
          message.destroy()
          message.error(graphQLErrorMessage(error) || '获取验证码失败')
        })
    },
    [form, store.userinfo, getMobileCodeAction]
  )

  const onConfirm = useCallback(async () => {
    const formData = await form.validateFields()

    setLoading(true)

    setTradePwdByMobileAction({
      variables: {
        code: formData.code,
        password: CryptoJS.MD5(formData.password).toString(),
      },
    })
      .then(() => {
        message.destroy()
        message.success('设置交易密码成功')
        store.userinfo?.setTradePwdStatus(true)
        setVisible(false)
      })
      .catch(error => {
        message.destroy()
        message.error(graphQLErrorMessage(error) || '设置交易密码失败')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [form])

  return (
    <MyModal
      visible={visible}
      title="设置交易密码"
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={448}
      footer={null}
    >
      <MyForm form={form} initialValues={initialValues}>
        <MyForm.Item
          name="mobile"
          rules={[
            {
              required: true,
              message: '请输入手机号',
            },
          ]}
        >
          <Input placeholder="手机号" disabled addonBefore="+86" />
        </MyForm.Item>
        <MyForm.Item
          name="code"
          rules={[{ required: true, message: '请输入手机验证码' }]}
        >
          <Input
            placeholder="手机验证码"
            addonAfter={
              <Captcha captchaId="get-pwd-captcha" onSuccess={onMobileCode} />
            }
          />
        </MyForm.Item>
        <MyForm.Item
          name="password"
          rules={[
            { required: true, message: '请输入交易密码' },
            { pattern: isTradePwd, message: '请输入6位数字交易密码' },
          ]}
        >
          <Input.Password placeholder="请输入6位数字交易密码" />
        </MyForm.Item>
        <MyForm.Item
          name="checkPassword"
          rules={[
            {
              validator: confirmPwd,
            },
          ]}
        >
          <Input.Password placeholder="确认交易密码" />
        </MyForm.Item>
        <MyBtn
          onClick={onConfirm}
          style={{ marginBottom: 16 }}
          type="primary"
          size="large"
          loading={loading}
          block
        >
          立即设置
        </MyBtn>
        <MyBtn onClick={() => setVisible(false)} type="link" block>
          取消设置交易密码
        </MyBtn>
      </MyForm>
    </MyModal>
  )
})
